import React, { useState, useEffect, useCallback } from "react";
import MainLogo from "../../assets/images/AstraLogo.svg";
import { mergeProfileIntoCachedUser } from "../../shared/_helpers/currentUser";
import { getSelectedPlanPriceId } from "../../shared/_helpers/selectedPlan";
import { updateSubscription, getAllPlansClient, getSetupFee, getProfile } from "../../shared/_services/common.services";
import { useSnackbar } from "notistack";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCardContentType } from "../../redux/stripeReducer";
import Loader from '../Loader';


function getPlanAmount(plan) {
  if (plan.amount) return plan.amount;
  if (plan.stripe?.unit_amount) return plan.stripe.unit_amount / 100;
  return 0;
}

function getPlanFeatures(plan) {
  return Array.isArray(plan.features) ? plan.features : [];
}

// The setup fee is one platform-level amount that applies to every package
// (backend SEC-110), so `check-fee` is the only thing that knows what a customer
// will be charged. `plan.setupFeePrice` and `plan.setupFeeEnable` are still
// stored on the plan but no billing path reads either one, so preferring them
// here quoted a number nobody would ever be billed — a plan carrying a stale $25
// showed $25 while the customer paid the platform amount.
//
// This is the same expression Payment.jsx:105 already uses. The disabled case
// resolves to 0 rather than a struck-through amount because the server charges
// nothing when the switch is off, and there is no per-plan waiver left to show.
export function getSetupFeeData(setupfee) {
  const setupFeePriceNum = setupfee?.isFeeEnabled ? Number(setupfee.setup_fee) : 0;
  const shouldShowSetupFee = !Number.isNaN(setupFeePriceNum) && setupFeePriceNum > 0;

  return {
    shouldShowSetupFee,
    setupFeePriceNum,
  };
}

function getPlanDisplayName(plan, index) {
  return plan.nickname || `Package ${index + 1}`;
}

function getPlanButtonLabel(isSelectedPlan) {
  return isSelectedPlan ? "Current Plan" : "Get Started";
}

function PackageCard({
  plan,
  index,
  activeTab,
  setupfee,
  isSelectedPlan,
  planId,
  packageType,
  handleClick,
}) {
  const isActive = activeTab === index;
  const planAmount = getPlanAmount(plan);
  const planFeatures = getPlanFeatures(plan);
  const planDisplayName = getPlanDisplayName(plan, index);
  const { shouldShowSetupFee, setupFeePriceNum } = getSetupFeeData(setupfee);

  return (
    <div
      key={planId}
      className={`package-box ${packageType} ${isActive ? "show" : "hide"}`}
    >
      <div className={`package-box-header ${plan.nickname || "Package"}`}>
        <h4 className="mb-0">{planDisplayName}</h4>
        {plan.description && (
          <small className="text-secondary f-400 text-14">{plan.description}</small>
        )}
        <h1 className="package-amount text-astra mt-3 mb-2">
          ${planAmount || 0}/
          <p> month</p>
        </h1>
        {shouldShowSetupFee && (
          <h6 className="setup-amount">
            Setup fee&nbsp;:
            <span style={{ marginLeft: "5px" }}>
              ${setupFeePriceNum.toFixed(2)}
            </span>
          </h6>
        )}
        <div className="text-center">
          <button
            type="submit"
            disabled={isSelectedPlan}
            className="as-btn-primary animated-btn w-100 mt-3"
            onClick={() => handleClick(planId)}
          >
            {getPlanButtonLabel(isSelectedPlan)}
          </button>
        </div>
      </div>
      {planFeatures.length > 0 && (
        <ul className="package-body mb-0">
          <p className="f-700 mb-2">What's included:</p>
          {planFeatures.map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SelectPackage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [planData, setPlanData] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [setupfee, setSetUpFee] = useState('');
  
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPackageLoader, setShowPackageLoader] = useState(false)
  const location = useLocation();
  const fromPage = location.state?.from;

  const clearSelectedPlan = useCallback(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser?.user?.selected_plan) {
      delete currentUser.user.selected_plan;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    }
  }, []);

  useEffect(() => {
    const handleBack = () => {
      clearSelectedPlan();
    };
    window.addEventListener('popstate', handleBack);
    return () => {
      window.removeEventListener('popstate', handleBack);
    };
  }, [clearSelectedPlan]);

  // One function for every entry path. It reads the plan from the cached
  // `selected_plan`, which is on the public allowlist (SEC-058/SEC-074). The
  // mobile `?token=` handoff that used to be a second entry path is deleted
  // (#94/#102/#103).
  const fetchPlanData = async () => {
    setShowPackageLoader(true)
    const selectedPlan = JSON.parse(localStorage.getItem('currentUser'))?.user?.selected_plan;
    let currentSelectedPlanId = null;
    if (selectedPlan) {
      currentSelectedPlanId = getSelectedPlanPriceId(selectedPlan);
      setSelectedPlanId(currentSelectedPlanId);
    }
    try {
      const response = await getAllPlansClient();
      if (response.status === 200 && Array.isArray(response.data.data)) {
        // Sort packages by sequence if available, otherwise by order received
        const sortedPlans = [...response.data.data].sort((a, b) => {
          const seqA = a.sequence || 999;
          const seqB = b.sequence || 999;
          return seqA - seqB;
        });
        // Filter only active plans
        const activePlans = sortedPlans.filter(plan => plan.active !== false);
        setPlanData(activePlans);
        // Set first package as active tab if no packages loaded
        if (activePlans.length > 0 && activeTab === 0 && !currentSelectedPlanId) {
          setActiveTab(0);
        }
      } else {
        enqueueSnackbar("Error while getting payment details!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      }
    } catch (error) {
      enqueueSnackbar("Error while getting payment details!", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      console.error('Failed to fetch plans:', error);
    } finally {
      setShowPackageLoader(false)
    }
  };
  const checkFee = async () => {
    const data = await getSetupFee();
    if (data.status === 200) {
      setSetUpFee(data?.data);
    }
  }
  // #99: the profile is the source of truth for `selected_plan`. The cache can
  // lack it (written before the purchase, or by a login payload that omitted it
  // before backend INT-083), so it is merged in *before* `fetchPlanData` reads
  // it. The merge re-reads the cache after the await so a token the silent
  // refresh rotated meanwhile is not overwritten with a stale copy.
  const fetchUser = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;
    if (!userId) return;
    const res = await getProfile(userId);
    if (res?.status !== 200) return;
    mergeProfileIntoCachedUser(res.data?.data?.user, userId);
  };
  useEffect(() => {
    // Sequenced, not parallel (#99): fetchPlanData reads `selected_plan` from the
    // cache, so it must run after the profile has been merged into it.
    (async () => {
      await fetchUser();
      fetchPlanData();
    })();
    checkFee();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPlanId = (plan) => {
    return plan.stripe.id;
  };

  const isCurrentPlan = (plan) => {
    const planId = getPlanId(plan);
    return selectedPlanId === planId;
  };

  const getPackageType = (index) => {
    if (index === 0) return 'essential';
    if (index === 1) return 'premium';
    return `package-${index}`;
  };

  const handleClick = async (planId) => {
    setShowPackageLoader(true)

    const selectedPlanData = planData.find(plan => 
      plan._id === planId || plan.id === planId || plan.stripePriceId === planId
    );

    if (!selectedPlanData) {
      enqueueSnackbar("Selected plan not found!", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowPackageLoader(false);
      return;
    }

    if (selectedPlanId) {
      // User already has a plan, update subscription
      let currentUser = JSON.parse(localStorage.getItem('currentUser'));
      const priceId = selectedPlanData.stripePriceId || selectedPlanData.stripe?.id || selectedPlanData.id;
      // `priceId` is the whole body now. `subscriptionId` used to come from
      // `stripe_details.subscriptionId`, which backend SEC-058 removed from every
      // user payload (#53) — but re-sourcing it would be the wrong fix: SEC-003
      // and SEC-107 made `change-plan` read the subscription id from the token
      // subject's own record and **403 a body value that disagrees with it**,
      // because a caller passing a stranger's subscription id repriced that
      // stranger at Stripe while the handler recorded the change against itself.
      // `userId` goes for the same reason. Both happened to pass while they were
      // sending `undefined`; sending a real value again would start failing.
      let data = await updateSubscription({ priceId });
      if (data.status === 200) {
        enqueueSnackbar("Subscription plan updated successfully.", {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });

          currentUser.user = data.data.userData;  // remove old user and replace with updated user
          localStorage.setItem("currentUser", JSON.stringify(currentUser));
          navigate('/profile');
       
       
      }
    } else {
      // New subscription purchase
      let currentUser = JSON.parse(localStorage.getItem('currentUser'));
      dispatch(setCardContentType("subscriptionPurchase"));
      currentUser.user.selected_plan = selectedPlanData;
      window.localStorage.setItem('currentUser', JSON.stringify(currentUser));
      if (fromPage !== 'changePlan') {
        navigate('/make-payment');
      } else {
        navigate('/make-payment', { state: { from: 'changePlan' } });
      }
    }
    setShowPackageLoader(false)
  }

  const renderPackageContent = () => {
    if (showPackageLoader) {
      return <Loader />;
    }

    if (planData.length === 0) {
      return (
        <div className="text-center text-light py-5">
          <p>No packages available at this time.</p>
        </div>
      );
    }

    return (
      <div className="package-box-wrap">
        {planData.map((plan, index) => {
          const planId = getPlanId(plan);
          const packageType = getPackageType(index);
          const isSelectedPlan = isCurrentPlan(plan);

          return (
            <PackageCard
              key={planId}
              plan={plan}
              index={index}
              activeTab={activeTab}
              setupfee={setupfee}
              isSelectedPlan={isSelectedPlan}
              planId={planId}
              packageType={packageType}
              handleClick={handleClick}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="package-payment-wrap package-page">
      <div className="package-logo text-center">
        <img className="package-payment-logo" src={MainLogo} alt="Main Logo" />
      </div>
      <h2 className="text-center package-page-heading text-light mb-0">
        <Link
        to={fromPage === "changePlan" ? "/profile" : fromPage}
          style={{ paddingRight: 15, color: "#fff" }}
          onClick={clearSelectedPlan}
        >
          <i className="iconoir-arrow-left"></i>
        </Link>
        Choose Your Investment Plan</h2>
      <p className="text-center text-light mb-5">Professional financial advisory tailored to your goals. Start your wealth-building journey today.</p>

      {/* Mobile Tabs */}
      {planData.length > 1 && (
        <div className="mobile-package-tabs">
          {planData.map((plan, index) => {
            const planId = getPlanId(plan);
            const isActive = activeTab === index;
            const packageType = getPackageType(index);
            return (
              <div
                key={planId}
                className={`package-tab ${packageType} ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {plan.nickname || `Package ${index + 1}`}
              </div>
            );
          })}
        </div>
      )}

      <div className="">
        {renderPackageContent()}
      </div>
    </div>
  );
};

export default SelectPackage;
